import { useMutation, useQueryClient, queryOptions } from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_EXPERIMENTS,
  ADMIN_EXPERIMENT,
} from "@/lib/graphql/queries/experiments";
import {
  ADMIN_CREATE_EXPERIMENT,
  ADMIN_UPDATE_EXPERIMENT,
  ADMIN_UPDATE_EXPERIMENT_ROLLOUT,
  ADMIN_DELETE_EXPERIMENT,
  ADMIN_UPDATE_EXPERIMENT_STATUS,
} from "@/lib/graphql/mutations/experiments";
import type {
  Experiment,
  CreateExperimentInput,
  UpdateExperimentInput,
  UpdateExperimentRolloutInput,
} from "@/types/experiment";
import { ExperimentStatus } from "@/types/enums";
import type { MutationResult } from "@/types/common";
import { toast } from "sonner";

export const experimentKeys = {
  all: ["experiments"] as const,
  lists: () => [...experimentKeys.all, "list"] as const,
  list: (status?: ExperimentStatus) =>
    [...experimentKeys.lists(), { status }] as const,
  details: () => [...experimentKeys.all, "detail"] as const,
  detail: (key: string) => [...experimentKeys.details(), key] as const,
};

export function experimentListQueryOptions(status?: ExperimentStatus) {
  return queryOptions({
    queryKey: experimentKeys.list(status),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminExperiments: Experiment[];
      }>(ADMIN_EXPERIMENTS, status ? { status } : undefined);

      return data.adminExperiments;
    },
    staleTime: 30000,
  });
}

export function experimentDetailQueryOptions(key: string) {
  return queryOptions({
    queryKey: experimentKeys.detail(key),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminExperiment: Experiment | null;
      }>(ADMIN_EXPERIMENT, { key });

      return data.adminExperiment;
    },
    staleTime: 30000,
  });
}

export function useCreateExperiment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateExperimentInput) => {
      const data = await authenticatedRequest<{
        adminCreateExperiment: MutationResult;
      }>(ADMIN_CREATE_EXPERIMENT, { input });

      return data.adminCreateExperiment;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: experimentKeys.all });
      toast.success(result.message || "실험이 생성되었습니다");
    },
    onError: (error: Error) => {
      toast.error("실험 생성 실패", { description: error.message });
    },
  });
}

export function useUpdateExperiment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateExperimentInput) => {
      const data = await authenticatedRequest<{
        adminUpdateExperiment: MutationResult;
      }>(ADMIN_UPDATE_EXPERIMENT, { input });

      return data.adminUpdateExperiment;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: experimentKeys.all });
      toast.success(result.message || "실험이 수정되었습니다");
    },
    onError: (error: Error) => {
      toast.error("실험 수정 실패", { description: error.message });
    },
  });
}

export function useUpdateExperimentRollout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateExperimentRolloutInput) => {
      const data = await authenticatedRequest<{
        adminUpdateExperimentRollout: MutationResult;
      }>(ADMIN_UPDATE_EXPERIMENT_ROLLOUT, { input });

      return data.adminUpdateExperimentRollout;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: experimentKeys.all });
      toast.success(result.message || "rollout이 조정되었습니다");
    },
    onError: (error: Error) => {
      toast.error("rollout 조정 실패", { description: error.message });
    },
  });
}

export function useDeleteExperiment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string) => {
      const data = await authenticatedRequest<{
        adminDeleteExperiment: MutationResult;
      }>(ADMIN_DELETE_EXPERIMENT, { key });

      return data.adminDeleteExperiment;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: experimentKeys.all });
      toast.success(result.message || "실험이 삭제되었습니다");
    },
    onError: (error: Error) => {
      toast.error("실험 삭제 실패", { description: error.message });
    },
  });
}

export function useUpdateExperimentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      key,
      status,
    }: {
      key: string;
      status: ExperimentStatus;
    }) => {
      const data = await authenticatedRequest<{
        adminUpdateExperimentStatus: MutationResult;
      }>(ADMIN_UPDATE_EXPERIMENT_STATUS, { key, status });

      return data.adminUpdateExperimentStatus;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: experimentKeys.all });
      toast.success(result.message || "상태가 변경되었습니다");
    },
    onError: (error: Error) => {
      toast.error("상태 변경 실패", { description: error.message });
    },
  });
}
